import type { GridApiPro, GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import type { Period, UsageReportV2ReportItem } from '@vy/types';
import { useCallback, useMemo, useState } from 'react';
import { VyTableLite } from '../../../../../components/new-design/table/vy-table/VyTable';
import { useUsageReportV2Report } from '../../../../../hooks/requests/use-usage-report-v2-report';
import { useTableColumnsManager } from '../../../../../hooks/use-table-columns-manager';
import type { FilterLineType } from '../../../../../components/new-design/vyTableFilter';
import { useFilteredUsageReportV2 } from '../../hooks/use-filtered-usage-report-v2';
import { buildUsageReportV2ColumnGroups } from './UsageReportV2ReportTableColumns';

const TABLE_NAME = 'usage-report-v2';
const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];
const DEFAULT_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: 100 };
const EMPTY_CUSTOM_FIELD_NAMES: { id: string; name: string }[] = [];
const EMPTY_REPORT_ITEMS: UsageReportV2ReportItem[] = [];

type UsageReportV2ReportTableProps = {
  period: Partial<Period>;
  searchText: string | null | undefined;
  filters: FilterLineType[];
  columns: GridColDef<UsageReportV2ReportItem>[];
  apiRef?: React.MutableRefObject<GridApiPro>;
};

export const UsageReportV2ReportTable: React.FC<UsageReportV2ReportTableProps> = ({
  period,
  searchText,
  filters,
  columns,
  apiRef,
}) => {
  const { data: report, isLoading, isFetching } = useUsageReportV2Report(period);
  const filteredReport = useFilteredUsageReportV2(report, filters, searchText);

  const columnGroupingModel = useMemo(
    () =>
      buildUsageReportV2ColumnGroups(
        report?.customerCustomFieldNames ?? EMPTY_CUSTOM_FIELD_NAMES,
        report?.contractCustomFieldNames ?? EMPTY_CUSTOM_FIELD_NAMES,
      ),
    [report?.customerCustomFieldNames, report?.contractCustomFieldNames],
  );

  const stableGetRowId = useCallback(
    (row: UsageReportV2ReportItem) =>
      `${row.contract.id}-${row.product.id}-${row.tier.index}-${row.period.startTime.toISOString()}`,
    [],
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);

  const { processedColumns } = useTableColumnsManager<UsageReportV2ReportItem>({
    apiRef,
    columns,
    tableName: TABLE_NAME,
  });

  return (
    <VyTableLite
      apiRef={apiRef}
      items={filteredReport?.reportItems ?? EMPTY_REPORT_ITEMS}
      columns={processedColumns}
      columnGroupingModel={columnGroupingModel}
      loadingItems={isLoading || isFetching}
      getRowId={stableGetRowId}
      density="compact"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  );
};
